import { useWorkspaceStore } from '@/store/workspaceStore';
import { SectionCard } from '@/components/SectionCard';
import { Select } from '@/components/Select';
import { Slider } from '@/components/Slider';
import { Toggle } from '@/components/Toggle';
import { ColorPicker } from '@/components/ColorPicker';
import { Button } from '@/components/Button';
import { CAPTION_PRESETS } from '@/lib/captionPresets';
import { defaultCaptionSettings } from '@/lib/defaults';
import { validateCaptionSettings } from '@/lib/validation';
import type { CaptionAnimation, CaptionCasing, CaptionPosition, CaptionSettings, ReelProject } from '@/types';

const FONT_OPTIONS = [
  'Inter',
  'Montserrat',
  'Poppins',
  'Bebas Neue',
  'Roboto Mono',
  'Alex Brush',
  'Allura',
  'Sacramento',
  'Dancing Script',
].map((f) => ({ value: f, label: f }));
const ANIMATION_OPTIONS: { value: CaptionAnimation; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'fade-in', label: 'Fade in' },
  { value: 'pop', label: 'Pop' },
  { value: 'slide-up', label: 'Slide up' },
  { value: 'word-highlight', label: 'Word highlight' },
  { value: 'current-word-emphasis', label: 'Current-word emphasis' },
];
const POSITION_OPTIONS: { value: CaptionPosition; label: string }[] = [
  { value: 'top', label: 'Top' },
  { value: 'middle', label: 'Middle' },
  { value: 'bottom', label: 'Bottom' },
  { value: 'custom', label: 'Custom (drag in preview)' },
];

export function CaptionSettingsPanel({ project }: { project: ReelProject }) {
  const updateCaptions = useWorkspaceStore((s) => s.updateCaptions);
  const shared = useWorkspaceStore((s) => s.workspace.shared);
  const c = project.captions.overridesShared ? project.captions : shared.captions;

  function patch(fields: Partial<CaptionSettings>) {
    updateCaptions(project.id, { ...fields, overridesShared: true });
  }

  const validation = validateCaptionSettings(c);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-lg bg-slate-100 px-3 py-2 text-xs dark:bg-slate-800">
        <span className="text-slate-500 dark:text-slate-400">Caption styling comes from shared defaults unless overridden here.</span>
        <label className="flex shrink-0 items-center gap-2">
          <input
            type="checkbox"
            checked={project.captions.overridesShared}
            onChange={(e) => updateCaptions(project.id, { overridesShared: e.target.checked })}
          />
          Override for this Reel
        </label>
      </div>

      <SectionCard title="Captions">
        <Toggle label="Enable captions" checked={c.enabled} onChange={(v) => patch({ enabled: v })} />
        {!c.enabled && <p className="text-xs text-slate-400">Captions are hidden for this Reel.</p>}
      </SectionCard>

      {c.enabled && (
        <>
          <SectionCard title="Preset">
            <div className="grid grid-cols-3 gap-2">
              {(Object.keys(CAPTION_PRESETS) as (keyof typeof CAPTION_PRESETS)[]).map((id) => (
                <button
                  key={id}
                  onClick={() => patch({ preset: id, ...CAPTION_PRESETS[id].patch })}
                  className={`rounded-lg border px-2 py-2 text-xs font-medium transition-colors ${
                    c.preset === id
                      ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                      : 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800'
                  }`}
                >
                  {CAPTION_PRESETS[id].label}
                </button>
              ))}
            </div>
          </SectionCard>

          <SectionCard title="Typography" onReset={() => patch(defaultCaptionSettings())}>
            <div className="grid grid-cols-2 gap-2">
              <Select label="Font" value={c.fontFamily} options={FONT_OPTIONS} onChange={(v) => patch({ fontFamily: v })} />
              <Select
                label="Weight"
                value={String(c.fontWeight)}
                options={[
                  { value: '400', label: 'Regular' },
                  { value: '600', label: 'Semibold' },
                  { value: '700', label: 'Bold' },
                  { value: '800', label: 'Extra bold' },
                ]}
                onChange={(v) => patch({ fontWeight: Number(v) as CaptionSettings['fontWeight'] })}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Slider label="Font size" value={c.fontSizePx} min={24} max={120} onChange={(v) => patch({ fontSizePx: v })} formatValue={(v) => `${v}px`} />
              <Slider label="Letter spacing" value={c.letterSpacingPx} min={-2} max={10} step={0.5} onChange={(v) => patch({ letterSpacingPx: v })} formatValue={(v) => `${v}px`} />
            </div>
            <Slider label="Line spacing" value={c.lineHeight} min={0.9} max={2} step={0.05} onChange={(v) => patch({ lineHeight: v })} />
            <div className="grid grid-cols-3 gap-2">
              <ColorPicker label="Text colour" value={c.textColor} onChange={(v) => patch({ textColor: v })} />
              <ColorPicker label="Outline colour" value={c.outlineColor} onChange={(v) => patch({ outlineColor: v })} />
              <Slider label="Outline thickness" value={c.outlineWidthPx} min={0} max={16} onChange={(v) => patch({ outlineWidthPx: v })} formatValue={(v) => `${v}px`} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <ColorPicker label="Background colour" value={c.backgroundColor} onChange={(v) => patch({ backgroundColor: v })} />
              <Slider label="Background opacity" value={c.backgroundOpacity} min={0} max={1} step={0.05} onChange={(v) => patch({ backgroundOpacity: v })} formatValue={(v) => `${Math.round(v * 100)}%`} />
            </div>
          </SectionCard>

          <SectionCard title="Casing">
            <div className="flex gap-2">
              {(['original', 'uppercase', 'lowercase'] as CaptionCasing[]).map((casing) => (
                <button
                  key={casing}
                  onClick={() => patch({ casing })}
                  className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-medium capitalize transition-colors ${
                    c.casing === casing
                      ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                      : 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800'
                  }`}
                >
                  {casing}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-400">Uppercase and lowercase are mutually exclusive — choose one, or keep the original casing.</p>
          </SectionCard>

          <SectionCard title="Layout & position">
            <Select label="Alignment" value={c.alignment} options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }]} onChange={(v) => patch({ alignment: v as CaptionSettings['alignment'] })} />
            <Select label="Vertical position" value={c.position} options={POSITION_OPTIONS} onChange={(v) => patch({ position: v as CaptionPosition })} />
            <div className="grid grid-cols-2 gap-2">
              <Slider label="Max characters/line" value={c.maxCharsPerLine} min={8} max={60} onChange={(v) => patch({ maxCharsPerLine: v })} />
              <Slider label="Max lines" value={c.maxLines} min={1} max={4} onChange={(v) => patch({ maxLines: v })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Slider label="Caption width" value={c.widthPercent} min={30} max={100} onChange={(v) => patch({ widthPercent: v })} formatValue={(v) => `${v}%`} />
              <Slider label="Safe-area margin" value={c.safeMarginPercent} min={0} max={30} onChange={(v) => patch({ safeMarginPercent: v })} formatValue={(v) => `${v}%`} tooltip="Keeps captions clear of TikTok/Instagram UI overlays." />
            </div>
            <p className="text-xs text-slate-400">Tip: drag the caption box directly in the preview player to reposition it precisely.</p>
          </SectionCard>

          <SectionCard title="Animation">
            <Select label="Animation style" value={c.animation} options={ANIMATION_OPTIONS} onChange={(v) => patch({ animation: v as CaptionAnimation })} />
            {(c.animation === 'word-highlight' || c.animation === 'current-word-emphasis') && (
              <ColorPicker label="Active word colour" value={c.activeWordColor} onChange={(v) => patch({ activeWordColor: v })} />
            )}
          </SectionCard>

          {!validation.valid && (
            <div className="rounded-lg bg-red-50 p-3 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
              <ul className="list-inside list-disc">
                {validation.errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          <Button size="sm" variant="ghost" onClick={() => patch(defaultCaptionSettings())}>
            Reset all caption settings
          </Button>
        </>
      )}
    </div>
  );
}
